
// -----------------------------------------------
// PDF Download

import { format } from "date-fns";
import { ROUTES, callAPI } from "../API/index";
import { TmpExcelDownload } from "./ServiceDownload";

// -----------------------------------------------
export const downloadPdf = async (type, params, filename) => {
    /**
     * PDF 다운로드 통합 서비스
     * type: 다운로드 타입
     * params: API 파라미터
     * filename: 다운로드 파일명
     */
    try {
        const response = await callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type, ...params });
        // 파일 다운로드 처리
        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/pdf'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return response;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PDF 처리 중 오류가 발생했습니다.'));
    }
}

export const downloadLeavePaymentPdf = async (payYear, payMonth, paymentType, filename) => {
    /**
     * 연차 지급대장 PDF 다운로드
     */
    const params = { payYear, payMonth, paymentType };
    const downloadFilename = filename || `연차지급대장_${payYear}_${payMonth}_${paymentType}.pdf`;

    try {
        const response = await downloadPdf('leave-payment', params, downloadFilename);

        // API 응답에서 오류 메시지 확인
        if (response && response.data && response.data.message) {
            // 오류 메시지가 있는 경우 에러로 처리
            const error = new Error(response.data.message);
            error.response = response;
            throw error;
        }

        return response;
    } catch (error) {
        console.error('PDF 다운로드 중 오류:', error);
        return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PDF 처리 중 오류가 발생했습니다.'));
    }
}

export const printLeavePaymentPdf = async (payYear, payMonth, paymentType) => {
    /**
     * 연차 지급대장 PDF 인쇄 (브라우저 인쇄 기능 사용)
     */
    try {
        const params = { payYear, payMonth, paymentType };

        const response = await callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type: 'leave-payment', ...params });

        // API 응답에서 오류 메시지 확인
        if (response && response.data) {
            console.log('API 응답 데이터:', response.data);
            console.log('응답 데이터 타입:', typeof response.data);
            console.log('Content-Type:', response.headers['content-type']);

            let messageData = response.data;

            // Content-Type이 application/json인 경우 response.data를 직접 확인
            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/json')) {
                console.log('JSON 응답 감지, response.data 직접 확인');

                // response.data가 Blob인 경우 텍스트로 변환
                if (response.data instanceof Blob) {
                    try {
                        const textData = await response.data.text();
                        console.log('Blob을 텍스트로 변환:', textData);

                        // JSON 파싱 시도
                        try {
                            messageData = JSON.parse(textData);
                            console.log('파싱된 메시지 데이터:', messageData);
                        } catch (e) {
                            console.log('JSON 파싱 실패:', e);
                            messageData = { message: textData }; // 텍스트를 message로 설정
                        }
                    } catch (e) {
                        console.log('Blob 텍스트 변환 실패:', e);
                    }
                }

                // 오류 메시지가 있는 경우 에러로 처리
                if (messageData && messageData.message) {
                    const error = new Error(messageData.message);
                    error.response = response;
                    throw error;
                }
            }

            // PDF Blob 생성
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/pdf'
            });

            // Blob URL 생성
            const url = window.URL.createObjectURL(blob);

            // 새 창에서 PDF 열기
            const printWindow = window.open(url, '_blank');

            if (printWindow) {
                // PDF 로딩 완료 후 인쇄 다이얼로그 실행
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                        // 인쇄 완료 후 URL 정리
                        printWindow.onafterprint = () => {
                            printWindow.close();
                            window.URL.revokeObjectURL(url);
                        };
                        // 사용자가 인쇄를 취소한 경우를 위한 백업 정리
                        setTimeout(() => {
                            if (!printWindow.closed) {
                                printWindow.close();
                            }
                            window.URL.revokeObjectURL(url);
                        }, 60000); // 1분 후 자동 정리
                    }, 1000);
                };
            } else {
                // 팝업이 차단된 경우 다운로드로 대체
                const link = document.createElement('a');
                link.href = url;
                link.download = `연차지급대장_${payYear}_${payMonth}_${paymentType}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                throw new Error('팝업이 차단되어 다운로드로 대체되었습니다. 팝업을 허용해주세요.');
            }

            return response;
        } else {
            throw new Error('PDF 데이터를 받지 못했습니다.');
        }
    } catch (error) {
        console.error('PDF 인쇄 중 오류:', error);
        console.error('에러 상세 정보:', {
            message: error.message,
            response: error.response,
            responseData: error.response?.data
        });
        
        // 이미 Error 객체이고 메시지가 있으면 그대로 전달 (axios 인터셉터에서 처리된 경우)
        if (error instanceof Error && error.message && error.message !== 'PDF 처리 중 오류가 발생했습니다.') {
            return Promise.reject(error);
        }
        
        // error.response.data에서 메시지 추출 시도
        if (error.response && error.response.data) {
            const errorData = error.response.data;
            if (errorData.message) {
                return Promise.reject(new Error(errorData.message));
            }
            if (errorData.error) {
                return Promise.reject(new Error(errorData.error));
            }
        }
        
        // 기본 에러 메시지
        return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PDF 처리 중 오류가 발생했습니다.'));
    }
}

export const downloadPaymentPdf = (payYear, payMonth, paymentType, employeeNos, filename) => {
    /**
     * 급여 PDF 다운로드
     */
    const params = { payYear, payMonth, paymentType, employeeNos };
    const downloadFilename = filename || `급여명세서_${payYear}_${payMonth}_${paymentType}.pdf`;
    return downloadPdf('payment', params, downloadFilename);
}

export const downloadLeavePdf = (payYear, payMonth, paymentType, filename, baseDate) => {
    /**
     * 연차 PDF 다운로드
     * @param {string} [baseDate] - 기준일 (yyyy-MM-dd), 일자 포함 조회·다운로드용
     */
    const params = { payYear, payMonth, paymentType };
    if (baseDate) {
        params.baseDate = baseDate;
        params.day = baseDate.split('-')[2];
    }
    const downloadFilename = filename || `연차조회_${payYear}_${payMonth}_${paymentType}.pdf`;
    return downloadPdf('leave', params, downloadFilename);
}

export const downloadAttendancePdf = (payYear, payMonth, employeeNos, filename) => {
    /**
     * 출퇴근 결과 조회 PDF 다운로드
     */
    const params = { payYear, payMonth, employeeNos, filename };
    const downloadFilename = filename || `출퇴근결과조회_${payYear}_${payMonth}.pdf`;
    return downloadPdf('attendance', params, downloadFilename);
}

export const downloadRetirementTransferPdf = (payYear, payMonth, emplNo, filename, transferData = {}) => {
    /**
     * 퇴직연금 이체의뢰서 PDF 다운로드
     */
    const params = { payYear, payMonth, filename, transferData };
    if (emplNo) {
        params.employeeNos = emplNo;
    }
    const downloadFilename = filename || `퇴직연금이체의뢰서_${payYear}_${payMonth}.pdf`;
    return downloadPdf('retirement-transfer', params, downloadFilename);
}

export const downloadRetirementPdf = (payYear, payMonth, filename, employeeNos = []) => {
    /**
     * 퇴직연금 PDF 다운로드
     */
    const params = { payYear, payMonth, filename };
    if (employeeNos && employeeNos.length > 0) {
        params.employeeNos = employeeNos.join(',');
    }
    const downloadFilename = filename || `퇴직연금_${payYear}_${payMonth}.pdf`;
    return downloadPdf('retirement', params, downloadFilename);
}

export const downloadPaymentYearPdf = (payYear, paymentType, employeeNos, filename) => {
    /**
     * 연간 급여지급급 PDF 다운로드
     */
    const params = { payYear };
    
    // paymentType이 배열인 경우 쉼표로 구분된 문자열로 변환
    if (Array.isArray(paymentType) && paymentType.length > 0) {
        params.paymentType = paymentType.join(',');
    } else if (paymentType) {
        params.paymentType = paymentType;
    }
    
    if (employeeNos && employeeNos.length > 0) {
        params.employeeNos = employeeNos;
    }
    
    const downloadFilename = filename || `급여지급내역_${payYear}.pdf`;
    return downloadPdf('payment-year', params, downloadFilename);
}

export const downloadDeparturePdf = (payYear, payMonth, employeeNos, filename) => {
    /**
     * 외국인 출국 PDF 다운로드
     */
    const params = { payYear, payMonth, employeeNos, filename };
    const downloadFilename = filename || `출국만기보험_${payYear}_${payMonth}.pdf`;
    return downloadPdf('departure', params, downloadFilename);
}

export const downloadTransferRequestPdf = (payYear, payMonth, paymentType, employeeNos, transferRequestData, filename) => {
    /**
     * 급여 이체의뢰서 PDF 다운로드
     */
    const params = {
        payYear,
        payMonth,
        paymentType,
        employeeNos,
        transferRequestData // 이체의뢰서 정보 포함
    };
    const downloadFilename = filename || `급여이체의뢰서_${payYear}_${payMonth}_${paymentType}.pdf`;
    return downloadPdf('transfer-request', params, downloadFilename);
}

export const downloadTransferPdf = (payYear, payMonth, paymentType, employeeNos, filename) => {
    /**
     * 급여 이체 PDF 다운로드
     */
    const params = { payYear, payMonth, paymentType, employeeNos };
    const downloadFilename = filename || `급여이체_${payYear}_${payMonth}_${paymentType}.pdf`;
    return downloadPdf('transfer', params, downloadFilename);
}

export const printTransferRequestPdf = async (payYear, payMonth, paymentType, employeeNos, transferRequestData) => {
    /**
     * 급여 이체의뢰서 PDF 인쇄 (브라우저 인쇄 기능 사용)
     */
    try {
        const params = {
            payYear,
            payMonth,
            paymentType,
            employeeNos,
            transferRequestData // 이체의뢰서 정보 포함
        };

        const response = await callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type: 'transfer-request', ...params });

        // PDF Blob 생성
        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/pdf'
        });

        // Blob URL 생성
        const url = window.URL.createObjectURL(blob);

        // 새 창에서 PDF 열기
        const printWindow = window.open(url, '_blank');

        if (printWindow) {
            // PDF 로딩 완료 후 인쇄 다이얼로그 실행
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                    // 인쇄 완료 후 URL 정리
                    printWindow.onafterprint = () => {
                        printWindow.close();
                        window.URL.revokeObjectURL(url);
                    };
                    // 사용자가 인쇄를 취소한 경우를 위한 백업 정리
                    setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close();
                        }
                        window.URL.revokeObjectURL(url);
                    }, 60000); // 1분 후 자동 정리
                }, 1000);
            };
        } else {
            // 팝업이 차단된 경우 다운로드로 대체
            const link = document.createElement('a');
            link.href = url;
            link.download = `급여이체의뢰서_${payYear}_${payMonth}_${paymentType}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            throw new Error('팝업이 차단되어 다운로드로 대체되었습니다. 팝업을 허용해주세요.');
        }

        return response;
    } catch (error) {
        console.error('PDF 인쇄 중 오류:', error);
        return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PDF 처리 중 오류가 발생했습니다.'));
    }
}

export const printLaborCostStatementPdf = async (selectDate) => {
    /**
     * 노무비내역서(일용직) PDF 인쇄 (API type: transfer-daily)
     * @param {Date} selectDate - 기준년월
     */
    try {
        const month = selectDate ? format(selectDate, 'yyyy-MM') : null;
        const year = selectDate ? format(selectDate, 'yyyy') : null;
        if (!month || !year) {
            return Promise.reject(new Error('기준년월을 선택해주세요.'));
        }
        const params = { month, year };
        const response = await callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type: 'transfer-daily', ...params });

        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.includes('application/json')) {
            let msg = 'PDF 생성에 실패했습니다.';
            try {
                const raw = response.data;
                const text = typeof raw === 'string' ? raw : (raw instanceof Blob ? await raw.text() : String(raw));
                const parsed = JSON.parse(text);
                if (parsed && (parsed.message || parsed.msg)) msg = parsed.message || parsed.msg;
            } catch (_) { /* ignore */ }
            return Promise.reject(new Error(msg));
        }

        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/pdf'
        });
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');

        if (printWindow) {
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                        window.URL.revokeObjectURL(url);
                    };
                    setTimeout(() => {
                        if (!printWindow.closed) printWindow.close();
                        window.URL.revokeObjectURL(url);
                    }, 60000);
                }, 1000);
            };
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = `노무비내역서_${year}_${month}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return Promise.reject(new Error('팝업이 차단되어 다운로드로 대체되었습니다.'));
        }
        return response;
    } catch (error) {
        console.error('노무비내역서 PDF 인쇄 중 오류:', error);
        return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PDF 처리 중 오류가 발생했습니다.'));
    }
};

export const downloadManualPdf = (filename, downloadType) => {
    /**
     * 사용설명서 PDF 다운로드
     */
    const params = { filename, downloadType };
    switch (downloadType) {
        case 'simple-manual':
            return downloadPdf('simple-manual', params, filename);
        case 'detailed-manual':
            return downloadPdf('detailed-manual', params, filename);
        case 'insurance-settlement':
            // 엑셀 다운로드로 변경 (PDF 엔드포인트 사용)
            const excelFilename1 = filename.replace('.pdf', '.xlsx');
            return TmpExcelDownload('insurance-settlement', params, excelFilename1);
        case 'insurance-voucher':
            // 엑셀 다운로드로 변경 (PDF 엔드포인트 사용)
            const excelFilename2 = filename.replace('.pdf', '.xlsx');
            return TmpExcelDownload('insurance-voucher', params, excelFilename2);
        case 'user-faq':
            return downloadPdf('user-faq', params, filename);
        default:
            return Promise.reject(new Error('잘못된 다운로드 타입입니다.'));
    }
}